<template>
    <pc-page-layout
        v-bind="layoutBinder"
        ref="layout">
        <pc-page-header
            v-bind:header="pageHeader"
            icon-class="menu-incentive_design" />
        <hr />
        <div class="mb-3">
            <pc-input
                id="IncentiveNameTextbox"
                ref="IncentiveNameTextbox"
                v-model="incentiveNameWrapper"
                v-bind:label="incentiveNameLabel"
                v-bind:readonly="incentiveNameIsReadOnly"
                required
                v-bind:validator="requiredValidator"
                v-on:blur="setNameValidated" />
        </div>
        <div class="mb-3">
            <div class="d-flex flex-wrap">
                <div class="me-3">
                    <pc-checkbox
                        id="UseStartDateCheckbox"
                        v-model="useStartDate"
                        v-bind:label="useStartDateLabel"
                        v-bind:readonly="viewOnly" />
                    <pc-date
                        id="StartDateInput"
                        ref="StartDateInput"
                        v-model="startDateStringWrapper"
                        v-bind:alt-label="startDateAltLabel"
                        v-bind:disabled="!useStartDate"
                        v-bind:readonly="viewOnly"
                        v-bind:validator="effectiveDateValidator"
                        v-bind:validation-enabled="useStartDate" />
                </div>
                <div>
                    <pc-checkbox
                        id="UseExpirationDateCheckbox"
                        v-model="useExpirationDate"
                        v-bind:label="useExpirationDateLabel"
                        v-bind:readonly="viewOnly" />
                    <pc-date
                        id="ExpirationDateInput"
                        ref="ExpirationDateInput"
                        v-model="expirationDateStringWrapper"
                        v-bind:alt-label="expirationDateAltLabel"
                        v-bind:disabled="!useExpirationDate"
                        v-bind:readonly="viewOnly"
                        v-bind:validator="effectiveDateValidator"
                        v-bind:validation-enabled="useExpirationDate" />
                </div>
            </div>
            <div
                v-if="effectiveDateRangeError"
                class="invalid-feedback d-block fw-bold">
                {{ expirationBeforeActivationErrorText }}
            </div>
            <div>
                <i> {{ effectiveDatesNote }} </i>
            </div>
            <div>
                <i> {{ timeZoneNote }} {{ programTimeZone }} </i>
            </div>
        </div>
        <conditions-capsule
            class="mb-3"
            v-bind:title="conditionsCapsuleTitle"
            v-bind:readonly="viewOnly"
            v-bind:conditions="conditions"
            v-bind:data-fields="dataFields"
            v-bind:operators="operators"
            v-bind:list-collection="listCollection"
            v-on:addcondition="addCondition"
            v-on:modifycondition="modifyCondition"
            v-on:removecondition="removeCondition" />
        <metadata-capsule
            v-if="metadataFieldsExist"
            class="mb-3"
            v-bind:readonly="viewOnly"
            v-bind:metadata-fields="metadataFields"
            v-bind:incentive-metadata="incentiveMetadata"
            v-bind:title="metadataCapsuleTitle"
            v-on:metadata-updated="setFormDirty" />
        <div class="mb-3 d-flex flex-wrap">
            <pc-select
                id="IncentiveTypeSelector"
                v-model="incentiveTypeIdWrapper"
                v-bind:label="incentiveTypeSelectLabel"
                v-bind:readonly="viewOnly"
                v-bind:options="incentiveDesignTypes"
                required
                v-bind:validator="requiredValidator"
                text-prop="Name"
                value-prop="TypeId"
                style="min-width: 212px">
                <option value="">
                    {{ genericNoSelectionOption }}
                </option>
            </pc-select>
        </div>
        <div v-if="optionIsAvailable(OPTION.IncentiveLength)">
            <div class="mb-3 d-flex flex-wrap">
                <pc-input
                    id="IncentiveLengthTextbox"
                    key="IncentiveLengthTextbox"
                    v-bind:model-value="optionDefinition(OPTION.IncentiveLength).Value"
                    v-bind:label="incentiveLengthLabel"
                    v-bind:readonly="viewOnly"
                    v-bind:data-formatter="amountDataFormatter"
                    required
                    v-bind:validator="requiredPositiveIntegerValidator"
                    inputmode="numeric"
                    v-on:update:model-value="setParameter(OPTION.IncentiveLength, PARAM.Value, $event)" />
            </div>
        </div>
        <div v-if="incentiveValueOptionIsAvailable">
            <div
                v-if="incentiveValueFormulaOptionIsAvailable"
                class="d-flex flex-wrap">
                <div class="me-3">
                    <pc-radio-group
                        id="IncentiveValueInputRadio"
                        key="IncentiveValueInputRadio"
                        v-model="useIncentiveValueFormula"
                        v-bind:label="incentiveValueLabel(1)"
                        v-bind:readonly="viewOnly"
                        v-bind:options="incentiveValueInputOptions"
                        required
                        name="IncentiveValueInputRadio"
                        inline />
                </div>
            </div>
            <div v-else>
                <pc-label
                    for-id="IncentiveValueTextbox"
                    v-bind:label="incentiveValueLabel(1)"
                    show-required />
            </div>
            <div class="me-2 d-flex-fill flex-wrap">
                <div
                    v-if="useIncentiveValueFormula"
                    class="col-4">
                    <pc-filtered-select
                        id="IncentiveValueFormulaSelect"
                        key="IncentiveValueFormulaSelect"
                        v-bind:model-value="optionDefinition(OPTION.IncentiveValueFormula).FormulaId"
                        v-bind:alt-label="incentiveValueFormulaSelectLabel"
                        v-bind:readonly="viewOnly"
                        v-bind:items="decimalFormulas"
                        v-bind:placeholder-text="decimalFormulaNoSelectionOptionText"
                        required
                        v-bind:validator="requiredValidator"
                        text-prop="Name"
                        value-prop="PublicId"
                        v-on:update:model-value="setParameter(OPTION.IncentiveValueFormula, PARAM.FormulaId, $event)">
                    </pc-filtered-select>
                </div>
                <div
                    v-else
                    class="col-4">
                    <pc-input
                        id="IncentiveValueTextbox"
                        key="IncentiveValueTextbox"
                        ref="IncentiveValueTextbox"
                        v-bind:model-value="optionDefinition(OPTION.IncentiveValueConstant).Value"
                        v-bind:alt-label="incentiveValueLabel(1)"
                        v-bind:readonly="viewOnly"
                        v-bind:data-formatter="amountDataFormatter"
                        required
                        v-bind:validator="requiredNonNegativeNumberValidator"
                        inputmode="decimal"
                        v-on:update:model-value="setParameter(OPTION.IncentiveValueConstant, PARAM.Value, $event)" />
                </div>
            </div>
            <div class="mb-3 fst-italic">
                {{ incentiveValueRoundingNote }}
            </div>
            <div v-if="isFlatIncentive && useIncentiveValueFormula">
                <pc-checkbox
                    id="ExposeFormulaCheckbox"
                    key="ExposeFormulaCheckbox"
                    class="mb-3"
                    v-bind:model-value="optionDefinition(OPTION.IncentiveValueFormula).OtherValue"
                    v-bind:label="exposeFormulaLabel"
                    v-bind:readonly="viewOnly"
                    v-on:update:model-value="setParameter(OPTION.IncentiveValueFormula, PARAM.OtherValue, $event)" />
            </div>
        </div>
        <div
            v-if="optionIsAvailable(OPTION.Tier1RatingCutoff)"
            v-bind:class="indentedClass"
            class="mb-3">
            <div class="d-flex flex-wrap">
                <pc-input
                    id="Tier1RatingCutoffTextbox"
                    key="Tier1RatingCutoffTextbox"
                    ref="Tier1RatingCutoffTextbox"
                    class="mb-3"
                    v-bind:model-value="optionDefinition(OPTION.Tier1RatingCutoff).Value"
                    v-bind:label="tier1RatingCutoffLabel"
                    v-bind:readonly="viewOnly"
                    required
                    v-bind:validator="tierRatingCutoffValidator"
                    inputmode="numeric"
                    v-on:update:model-value="onTierRatingCutoffChanged(OPTION.Tier1RatingCutoff, $event)" />
            </div>
            <div class="d-flex flex-wrap">
                <pc-select
                    id="Tier1RatingCutoffDataFieldSelector"
                    key="Tier1RatingCutoffDataFieldSelector"
                    v-bind:model-value="optionDefinition(OPTION.Tier1RatingCutoff).DataFieldId"
                    v-bind:label="tier1RatingCutoffDataFieldSelectLabel"
                    v-bind:readonly="viewOnly"
                    v-bind:options="ratingDataFields"
                    required
                    v-bind:validator="requiredValidator"
                    text-prop="Name"
                    value-prop="PublicId"
                    v-on:update:model-value="setParameter(OPTION.Tier1RatingCutoff, PARAM.DataFieldId, $event)">
                    <option value="">
                        {{ ratingDataFieldNoSelectionOptionText }}
                    </option>
                </pc-select>
            </div>
            <div class="fst-italic">
                {{ tier1RatingCutoffNote }}
            </div>
        </div>
        <div
            v-if="optionIsAvailable(OPTION.Tier2Rate)"
            class="mb-3">
            <div class="d-flex flex-wrap">
                <div>
                    <pc-input
                        id="Tier2IncentiveRateTextbox"
                        key="Tier2IncentiveRateTextbox"
                        v-bind:model-value="optionDefinition(OPTION.Tier2Rate).Value"
                        v-bind:label="incentiveValueLabel(2)"
                        v-bind:readonly="viewOnly"
                        v-bind:data-formatter="amountDataFormatter"
                        required
                        v-bind:validator="requiredNonNegativeNumberValidator"
                        inputmode="decimal"
                        v-on:update:model-value="setParameter(OPTION.Tier2Rate, PARAM.Value, $event)" />
                </div>
            </div>
            <div class="fst-italic">
                {{ tier2IncentiveRateNote }}
            </div>
        </div>
        <div
            v-if="optionIsAvailable(OPTION.Tier2RatingCutoff)"
            v-bind:class="indentedClass"
            class="mb-3">
            <div class="d-flex flex-wrap">
                <pc-input
                    id="Tier2RatingCutoffTextbox"
                    key="Tier2RatingCutoffTextbox"
                    ref="Tier2RatingCutoffTextbox"
                    class="me-2"
                    v-bind:model-value="optionDefinition(OPTION.Tier2RatingCutoff).Value"
                    v-bind:label="tier2RatingCutoffLabel"
                    v-bind:readonly="viewOnly"
                    required
                    v-bind:validator="tierRatingCutoffValidator"
                    inputmode="numeric"
                    v-on:update:model-value="onTierRatingCutoffChanged(OPTION.Tier2RatingCutoff, $event)" />
            </div>
            <div
                v-if="tierRatingCutoffError"
                class="invalid-feedback d-block fw-bold">
                {{ tierRatingCutoffErrorText }}
            </div>
            <div class="fst-italic">
                {{ tier2RatingCutoffNote }}
            </div>
        </div>
        <div
            v-if="optionIsAvailable(OPTION.RatingDataField)"
            class="mb-3">
            <div class="d-flex flex-wrap">
                <pc-filtered-select
                    id="RatingDataFieldSelect"
                    key="RatingDataFieldSelect"
                    class="col-4"
                    v-bind:model-value="optionDefinition(OPTION.RatingDataField).DataFieldId"
                    v-bind:label="incentiveValueDataFieldLabel"
                    v-bind:readonly="viewOnly"
                    v-bind:items="incentiveValueDataFields"
                    v-bind:placeholder-text="genericNoSelectionOption"
                    required
                    v-bind:validator="requiredValidator"
                    text-prop="Name"
                    value-prop="PublicId"
                    v-on:update:model-value="setParameter(OPTION.RatingDataField, PARAM.DataFieldId, $event)">
                </pc-filtered-select>
            </div>
            <div class="fst-italic">
                {{ incentiveValueDataFieldNote }}
            </div>
        </div>
        <div v-if="optionIsAvailable(OPTION.AvgInvEfficiency)">
            <div class="mb-3">
                <div class="d-flex flex-wrap">
                    <div>
                        <pc-select
                            id="EfficiencyDataFieldSelect"
                            key="EfficiencyDataFieldSelect"
                            v-bind:model-value="optionDefinition(OPTION.AvgInvEfficiency).DataFieldId"
                            v-bind:label="avgInvEfficiencyDataFieldLabel"
                            v-bind:readonly="viewOnly"
                            v-bind:options="efficiencyDataFields"
                            required
                            v-bind:validator="requiredValidator"
                            text-prop="Name"
                            value-prop="PublicId"
                            v-on:update:model-value="setParameter(OPTION.AvgInvEfficiency, PARAM.DataFieldId, $event)">
                            <option value="">
                                {{ efficiencyDataFieldNoSelectionOptionText }}
                            </option>
                        </pc-select>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="optionIsAvailable(OPTION.AnnualDegradation)"
            class="mb-3">
            <div class="d-flex flex-wrap">
                <pc-input
                    id="AnnualDegradationTextbox"
                    key="AnnualDegradationTextbox"
                    v-bind:model-value="optionDefinition(OPTION.AnnualDegradation).Value"
                    v-bind:label="annualDegradationLabel"
                    v-bind:readonly="viewOnly"
                    v-bind:data-formatter="amountDataFormatter"
                    required
                    v-bind:validator="requiredNonNegativeNumberValidator"
                    inputmode="numeric"
                    v-on:update:model-value="setParameter(OPTION.AnnualDegradation, PARAM.Value, $event)" />
            </div>
            <div class="fst-italic">
                {{ annualDegradationNote }}
            </div>
        </div>
        <div
            v-if="optionIsAvailable(OPTION.MaximumIncentivizedRating)"
            class="mb-3">
            <div>
                <pc-checkbox
                    id="UseMaximumIncentivizedRatingCheckbox"
                    key="UseMaximumIncentivizedRatingCheckbox"
                    v-bind:model-value="optionDefinition(OPTION.MaximumIncentivizedRating).IsUsed"
                    v-bind:label="maximumIncentivizedRatingLabel"
                    v-bind:readonly="viewOnly"
                    v-bind:required="optionDefinition(OPTION.MaximumIncentivizedRating).IsUsed"
                    v-on:update:model-value="setParameter(OPTION.MaximumIncentivizedRating, PARAM.IsUsed, $event)" />
            </div>
            <div
                v-if="optionDefinition(OPTION.MaximumIncentivizedRating).IsUsed"
                v-bind:class="indentedClass"
                class="d-flex flex-wrap">
                <pc-input
                    id="MaximumIncentivizedRatingTextbox"
                    key="MaximumIncentivizedRatingTextbox"
                    v-bind:model-value="optionDefinition(OPTION.MaximumIncentivizedRating).Value"
                    v-bind:alt-label="maximumIncentivizedRatingLabel"
                    v-bind:readonly="viewOnly"
                    v-bind:data-formatter="amountDataFormatter"
                    required
                    v-bind:validator="requiredPositiveNumberValidator"
                    inputmode="numeric"
                    v-on:update:model-value="setParameter(OPTION.MaximumIncentivizedRating, PARAM.Value, $event)" />
            </div>
        </div>
        <div
            v-if="optionIsAvailable(OPTION.DesignFactor)"
            class="mb-3">
            <pc-checkbox
                id="UseDesignFactorCheckbox"
                key="UseDesignFactorCheckbox"
                v-bind:model-value="optionDefinition(OPTION.DesignFactor).IsUsed"
                v-bind:label="useDesignFactorLabel"
                v-bind:readonly="viewOnly"
                v-on:update:model-value="setParameter(OPTION.DesignFactor, PARAM.IsUsed, $event)" />
        </div>
        <div
            v-if="optionIsAvailable(OPTION.DesignFactorWithCaps)"
            class="mb-3">
            <pc-checkbox
                id="UseDesignFactorWithCapsCheckbox"
                key="UseDesignFactorWithCapsCheckbox"
                v-bind:model-value="optionDefinition(OPTION.DesignFactorWithCaps).IsUsed"
                v-bind:label="useDesignFactorLabel"
                v-bind:readonly="viewOnly"
                v-on:update:model-value="setParameter(OPTION.DesignFactorWithCaps, PARAM.IsUsed, $event)" />
            <div v-if="optionDefinition(OPTION.DesignFactorWithCaps).IsUsed">
                <div v-bind:class="indentedClass">
                    <div class="d-flex flex-wrap">
                        <pc-input
                            id="DesignFactorThresholdTextbox"
                            key="DesignFactorThresholdTextbox"
                            class="me-2"
                            v-bind:model-value="optionDefinition(OPTION.DesignFactorWithCaps).OtherValue"
                            v-bind:label="designFactorThresholdLabel"
                            v-bind:readonly="viewOnly"
                            inputmode="numeric"
                            v-on:update:model-value="
                                setParameter(OPTION.DesignFactorWithCaps, PARAM.OtherValue, $event)
                            " />
                    </div>
                    <div class="fst-italic mb-1">
                        {{ designFactorThresholdNote }}
                    </div>
                    <div class="d-flex flex-wrap">
                        <pc-input
                            id="DesignFactorMaximumTextbox"
                            key="DesignFactorMaximumTextbox"
                            class="me-2"
                            v-bind:model-value="optionDefinition(OPTION.DesignFactorWithCaps).Value"
                            v-bind:label="designFactorMaximumLabel"
                            v-bind:readonly="viewOnly"
                            inputmode="numeric"
                            v-on:update:model-value="setParameter(OPTION.DesignFactorWithCaps, PARAM.Value, $event)" />
                    </div>
                    <div class="fst-italic">
                        {{ designFactorMaximumNote }}
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="optionIsAvailable(OPTION.ProjectCostPercentageCap)"
            class="mb-3">
            <pc-checkbox
                id="UseProjectCostCapCheckbox"
                key="UseProjectCostCapCheckbox"
                v-bind:model-value="optionDefinition(OPTION.ProjectCostPercentageCap).IsUsed"
                v-bind:label="useProjectCostCapLabel"
                v-bind:disabled="costDataFields.length === 0"
                v-bind:readonly="viewOnly"
                v-on:update:model-value="setParameter(OPTION.ProjectCostPercentageCap, PARAM.IsUsed, $event)" />
            <div
                v-if="optionDefinition(OPTION.ProjectCostPercentageCap).IsUsed"
                v-bind:class="indentedClass">
                <div>
                    <div class="d-flex flex-wrap text-nowrap">
                        <pc-select
                            id="ProjectCostDataFieldSelect"
                            key="ProjectCostDataFieldSelect"
                            class="me-3 mb-1"
                            v-bind:model-value="optionDefinition(OPTION.ProjectCostPercentageCap).DataFieldId"
                            v-bind:label="projectCostDataFieldLabel"
                            v-bind:readonly="viewOnly"
                            v-bind:options="costDataFields"
                            required
                            v-bind:validator="requiredValidator"
                            text-prop="Name"
                            value-prop="PublicId"
                            v-on:update:model-value="
                                setParameter(OPTION.ProjectCostPercentageCap, PARAM.DataFieldId, $event)
                            ">
                            <option value="">
                                {{ costDataFieldNoSelectionOptionText }}
                            </option>
                        </pc-select>
                    </div>
                    <div class="d-flex flex-wrap text-nowrap">
                        <pc-input
                            id="ProjectCostMaxPctTextbox"
                            key="ProjectCostMaxPctTextbox"
                            class="me-2"
                            v-bind:model-value="optionDefinition(OPTION.ProjectCostPercentageCap).Value"
                            v-bind:label="projectCostMaximumPctLabel"
                            v-bind:readonly="viewOnly"
                            v-bind:data-formatter="amountDataFormatter"
                            required
                            v-bind:validator="requiredPositiveNumberValidator"
                            inputmode="numeric"
                            v-on:update:model-value="
                                setParameter(OPTION.ProjectCostPercentageCap, PARAM.Value, $event)
                            " />
                    </div>
                </div>
            </div>
            <div
                v-if="costDataFields.length === 0"
                v-bind:class="indentedClass"
                class="fst-italic">
                {{ projectCostDataFieldsNotAvailableNote }}
            </div>
        </div>
        <div
            v-if="optionIsAvailable(OPTION.AnnualUsageCap)"
            class="mb-3">
            <pc-checkbox
                id="UseAnnualUsageCapCheckbox"
                key="UseAnnualUsageCapCheckbox"
                v-bind:model-value="optionDefinition(OPTION.AnnualUsageCap).IsUsed"
                v-bind:label="useAnnualUsageCapLabel"
                v-bind:disabled="usageDataFields.length === 0"
                v-bind:readonly="viewOnly"
                v-on:update:model-value="setParameter(OPTION.AnnualUsageCap, PARAM.IsUsed, $event)" />
            <div
                v-if="optionDefinition(OPTION.AnnualUsageCap).IsUsed"
                v-bind:class="indentedClass">
                <div class="mb-3 d-flex flex-wrap">
                    <pc-select
                        id="AnnualUsageDataFieldSelect"
                        key="AnnualUsageDataFieldSelect"
                        v-bind:model-value="optionDefinition(OPTION.AnnualUsageCap).DataFieldId"
                        v-bind:label="annualUsageDataFieldLabel"
                        v-bind:readonly="viewOnly"
                        v-bind:options="usageDataFields"
                        required
                        v-bind:validator="requiredValidator"
                        text-prop="Name"
                        value-prop="PublicId"
                        v-on:update:model-value="setParameter(OPTION.AnnualUsageCap, PARAM.DataFieldId, $event)">
                        <option value="">
                            {{ usageDataFieldNoSelectionOptionText }}
                        </option>
                    </pc-select>
                </div>
            </div>
            <div
                v-if="usageDataFields.length === 0"
                v-bind:class="indentedClass"
                class="fst-italic">
                {{ annualUsageDataFieldsNotAvailableNote }}
            </div>
        </div>
        <div
            v-if="optionIsAvailable(OPTION.MaximumAmountCap)"
            class="mb-3">
            <pc-checkbox
                id="UseMaximumAmountCapCheckbox"
                key="UseMaximumAmountCapCheckbox"
                class="me-3"
                v-bind:model-value="optionDefinition(OPTION.MaximumAmountCap).IsUsed"
                v-bind:label="maximumAmountCapLabel"
                v-bind:readonly="viewOnly"
                v-bind:required="optionDefinition(OPTION.MaximumAmountCap).IsUsed"
                v-on:update:model-value="setParameter(OPTION.MaximumAmountCap, PARAM.IsUsed, $event)" />
            <div
                v-if="optionDefinition(OPTION.MaximumAmountCap).IsUsed"
                v-bind:class="indentedClass"
                class="d-flex flex-wrap">
                <pc-input
                    v-if="optionDefinition(OPTION.MaximumAmountCap).IsUsed"
                    id="MaximumAmountCapTextbox"
                    key="MaximumAmountCapTextbox"
                    class="d-flex flex-wrap"
                    v-bind:model-value="optionDefinition(OPTION.MaximumAmountCap).Value"
                    v-bind:alt-label="maximumAmountCapLabel"
                    v-bind:readonly="viewOnly"
                    v-bind:data-formatter="amountDataFormatter"
                    required
                    v-bind:validator="requiredPositiveNumberValidator"
                    inputmode="numeric"
                    v-on:update:model-value="setParameter(OPTION.MaximumAmountCap, PARAM.Value, $event)" />
            </div>
        </div>
        <div
            v-if="approvedAmountCapOptionIsAvailable"
            class="mb-3">
            <pc-checkbox
                id="UseApprovedAmountCapCheckbox"
                key="UseApprovedAmountCapCheckbox"
                v-bind:model-value="optionDefinition(OPTION.ApprovedAmountCap).IsUsed"
                v-bind:label="approvedAmountCapLabel"
                v-bind:readonly="viewOnly"
                v-on:update:model-value="setParameter(OPTION.ApprovedAmountCap, PARAM.IsUsed, $event)" />
        </div>
        <div>
            <pc-alert
                v-model:shown="showSaveIncentiveError"
                class="mb-3"
                v-bind:message="saveIncentiveDesignError"
                v-bind:severity="errorSeverity" />
        </div>
        <div class="d-flex flex-row justify-content-end">
            <pc-button
                id="CancelOrCloseButton"
                class="me-2"
                v-on:click="cancelChanges">
                {{ cancelOrCloseButtonText }}
            </pc-button>
            <div v-if="isNormalAdminEdit">
                <pc-button
                    id="SaveButton"
                    key="SaveButton"
                    primary
                    v-on:click="saveDesign">
                    {{ saveButtonText }}
                </pc-button>
            </div>
            <div v-if="isCpAdminOverrideEdit">
                <pc-button
                    id="CpAdminSaveButton"
                    key="CpAdminSaveButton"
                    warning
                    v-on:click="saveDesign">
                    {{ saveButtonText }}
                </pc-button>
            </div>
        </div>
    </pc-page-layout>
</template>

<script>
    import pageBase from "Components/page/_page-base.js";
    import conditionsCapsule from "./conditionsCapsule.vue";
    import metadataCapsule from "./metadataCapsule.vue";
    import { THOUSAND_SEPARATOR_FORMATTER } from "Util/dataFormatters";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { ValidationHub } from "Validation/validationHub";
    import { dateStrToClientDate } from "Util/clientDate";
    import { ALERT_SEVERITY } from "Util/alerts";
    import { KEYS, NAMESPACE } from "./i18n/editIncentiveDesign.i18n";
    import { mapI18n } from "Util/i18n";

    import textConfirmationDialog, {
        TEXT_CONFIRMATION_RESULT
    } from "ProgramAdmin/Components/textConfirmationDialog.vue";

    const NEW_INCENTIVE_DESIGN_PUBLIC_ID = "-1";

    export const NEW_CONDITION_TAG = "condition";

    export const INCENTIVE_TYPE = {
        OneTime: 0,
        PBI: 1,
        Flat: 2,
        CGBOneRatePBI: 3,
        CGBTieredPBI: 4,
        CGBTieredEPBB: 5
    };

    const OPTION = {
        IncentiveLength: 0x1,
        IncentiveValueConstant: 0x2,
        IncentiveValueFormula: 0x4,
        RatingDataField: 0x8,
        AnnualDegradation: 0x10,
        MaximumIncentivizedRating: 0x20,
        DesignFactor: 0x40,
        DesignFactorWithCaps: 0x80,
        ProjectCostPercentageCap: 0x100,
        AnnualUsageCap: 0x200,
        MaximumAmountCap: 0x400,
        ApprovedAmountCap: 0x800,
        Tier1RatingCutoff: 0x10000,
        Tier2Rate: 0x20000,
        Tier2RatingCutoff: 0x40000,
        AvgInvEfficiency: 0x80000
    };

    const PARAM = {
        IsUsed: "IsUsed",
        Value: "Value",
        OtherValue: "OtherValue",
        DataFieldId: "DataFieldId",
        FormulaId: "FormulaId"
    };

    const VALUE_INPUT_TYPE = {
        Constant: 0,
        Formula: 1
    };

    export default {
        name: "EditIncentiveDesign",
        components: {
            conditionsCapsule,
            metadataCapsule
        },
        extends: pageBase,
        provide() {
            return {
                editIncentiveDesign: this,
                validationHub: this.validationHub
            };
        },
        setup: pageBase.setup,
        data() {
            const requiredValidator = getValidator(this.$app, VALIDATOR_TYPES.required);

            const effectiveDateValidatorOptions = [{ type: VALIDATOR_TYPES.required }, { type: VALIDATOR_TYPES.date }];
            const effectiveDateValidator = getValidator(this.$app, effectiveDateValidatorOptions);

            const tierRatingCutoffValidatorOptions = [
                { type: VALIDATOR_TYPES.required },
                { type: VALIDATOR_TYPES.positiveNumber }
            ];

            const tierRatingCutoffValidator = getValidator(this.$app, tierRatingCutoffValidatorOptions);

            const requiredPositiveIntegerValidatorOptions = [
                { type: VALIDATOR_TYPES.required },
                { type: VALIDATOR_TYPES.positiveInteger, acceptThousandSeparated: true }
            ];
            const requiredPositiveIntegerValidator = getValidator(this.$app, requiredPositiveIntegerValidatorOptions);

            const requiredPositiveNumberValidatorOptions = [
                { type: VALIDATOR_TYPES.required },
                { type: VALIDATOR_TYPES.positiveNumber, acceptThousandSeparated: true }
            ];
            const requiredPositiveNumberValidator = getValidator(this.$app, requiredPositiveNumberValidatorOptions);

            const requiredNonNegativeNumberValidatorOptions = [
                { type: VALIDATOR_TYPES.required },
                { type: VALIDATOR_TYPES.nonNegativeNumber, acceptThousandSeparated: true }
            ];
            const requiredNonNegativeNumberValidator = getValidator(
                this.$app,
                requiredNonNegativeNumberValidatorOptions
            );

            return {
                // Set to true to use indents to indicate inputs and sub-inputs,
                //  false to left-justify everything
                OPTION: {},
                PARAM: {},
                useIndents: true,
                incentiveDesignTypes: [],
                programId: null,
                incentiveDesignId: null,
                programTimeZone: null,
                viewOnly: null,
                isEditable: false,
                incentiveDesignListUrl: null,
                incentiveName: null,
                useStartDate: false,
                startDateString: null,
                useExpirationDate: false,
                expirationDateString: null,
                conditions: [],
                dataFields: [],
                operators: {},
                listCollection: {},
                metadataFields: [],
                incentiveMetadata: [],
                nameValidated: false,
                incentiveTypeId: "",
                optionDefinitions: [],
                useIncentiveValueFormula: 0,
                requiredValidator,
                effectiveDateValidator,
                effectiveDateRangeError: false,
                tierRatingCutoffValidator,
                tierRatingCutoffError: false,
                requiredPositiveIntegerValidator,
                requiredPositiveNumberValidator,
                requiredNonNegativeNumberValidator,
                validationHub: new ValidationHub(),
                validationResults: null,
                saveIncentiveDesignError: "",
                loaded: false
            };
        },
        computed: {
            model() {
                return this.Model || {};
            },
            costDataFields() {
                return this.model.CostDataFields || {};
            },
            costDataFieldNoSelectionOptionText() {
                return this.noSelectionOptionText(this.costDataFields.length);
            },
            decimalFormulas() {
                return this.model.DecimalFormulas || {};
            },
            decimalFormulaNoSelectionOptionText() {
                return this.noSelectionOptionText(this.decimalFormulas.length);
            },
            efficiencyDataFields() {
                return this.model.EfficiencyDataFields || {};
            },
            efficiencyDataFieldNoSelectionOptionText() {
                return this.noSelectionOptionText(this.efficiencyDataFields.length);
            },
            productionDataFields() {
                return this.model.ProductionDataFields || {};
            },
            productionDataFieldNoSelectionOptionText() {
                return this.noSelectionOptionText(this.productionDataFields.length);
            },
            ratingDataFields() {
                return this.model.RatingDataFields || {};
            },
            ratingDataFieldNoSelectionOptionText() {
                return this.noSelectionOptionText(this.ratingDataFields.length);
            },
            incentiveValueDataFieldNoSelectionOptionText() {
                return this.noSelectionOptionText(this.incentiveValueDataFields.length);
            },
            usageDataFields() {
                return this.model.UsageDataFields || {};
            },
            usageDataFieldNoSelectionOptionText() {
                return this.noSelectionOptionText(this.usageDataFields.length);
            },
            amountDataFormatter() {
                return THOUSAND_SEPARATOR_FORMATTER;
            },
            pageHeader() {
                if (this.viewOnly) {
                    return this.viewIncentiveDesignHeader;
                } else {
                    return this.editIncentiveDesignHeader;
                }
            },
            breadcrumbModel() {
                return {
                    Entries: [
                        {
                            Display: this.parentPageBreadcrumb,
                            Url: this.incentiveDesignListUrl
                        },
                        {
                            Display: this.pageHeader,
                            Url: null
                        }
                    ]
                };
            },
            metadataFieldsExist() {
                return this.metadataFields && this.metadataFields.length > 0;
            },
            indentedClass() {
                return this.useIndents ? "ms-4" : "";
            },
            incentiveTypeIdWrapper: {
                get() {
                    return this.incentiveTypeId;
                },
                set(value) {
                    this.incentiveTypeId = value;
                    this.onIncentiveTypeChanged();
                }
            },
            incentiveNameIsReadOnly() {
                // If the page is ViewOnly then of course the IncentiveName is ReadOnly.
                // However, a CPWriteAdmin may be editing an IncentiveDesign which is not Editable for Program
                //  Admins.  But even in that case the IncentiveName will not be editable unless the IncentiveDesign
                //  is fully Editable.  Once an IncentiveDesign has been Enabled, the IncentiveName cannot be
                //  changed by *anyone*.
                return this.viewOnly || !this.isEditable;
            },
            incentiveNameWrapper: {
                get() {
                    return this.incentiveName;
                },
                set(value) {
                    this.incentiveName = value;
                    this.setFormDirty();
                }
            },
            startDateStringWrapper: {
                get() {
                    return this.startDateString;
                },
                set(value) {
                    this.startDateString = value;
                    this.onEffectiveDateChanged();
                }
            },
            expirationDateStringWrapper: {
                get() {
                    return this.expirationDateString;
                },
                set(value) {
                    this.expirationDateString = value;
                    this.onEffectiveDateChanged();
                }
            },
            selectedDesignType() {
                let selected = null;
                if (this.incentiveDesignTypes && !isNaN(this.incentiveTypeId)) {
                    selected = this.incentiveDesignTypes.find((t) => t.TypeId === this.incentiveTypeId);
                }
                return selected;
            },
            availableOptions() {
                let options = 0;
                if (this.selectedDesignType) {
                    options = this.selectedDesignType.Options;
                }
                return options;
            },
            isFlatIncentive() {
                return this.incentiveTypeId === INCENTIVE_TYPE.Flat;
            },
            isPbiIncentive() {
                return (
                    this.incentiveTypeId === INCENTIVE_TYPE.PBI ||
                    this.incentiveTypeId === INCENTIVE_TYPE.CGBOneRatePBI ||
                    this.incentiveTypeId === INCENTIVE_TYPE.CGBTieredPBI
                );
            },
            isEpbbIncentive() {
                return (
                    this.incentiveTypeId === INCENTIVE_TYPE.OneTime ||
                    this.incentiveTypeId === INCENTIVE_TYPE.CGBTieredEPBB
                );
            },
            isTieredIncentive() {
                return this.optionIsAvailable(OPTION.Tier2Rate);
            },
            isCustomIncentive() {
                return (
                    this.incentiveTypeId === INCENTIVE_TYPE.CGBOneRatePBI ||
                    this.incentiveTypeId === INCENTIVE_TYPE.CGBTieredPBI ||
                    this.incentiveTypeId === INCENTIVE_TYPE.CGBTieredEPBB
                );
            },
            incentiveValueOptionIsAvailable() {
                return (
                    this.optionIsAvailable(OPTION.IncentiveValueConstant) || this.incentiveValueFormulaOptionIsAvailable
                );
            },
            incentiveValueFormulaOptionIsAvailable() {
                return this.optionIsAvailable(OPTION.IncentiveValueFormula) && this.decimalFormulas.length > 0;
            },
            incentiveValueInputOptions() {
                return [
                    { text: this.incentiveValueInputConstantOption, value: VALUE_INPUT_TYPE.Constant },
                    { text: this.incentiveValueSelectFormulaOption, value: VALUE_INPUT_TYPE.Formula }
                ];
            },
            incentiveValueRoundingNote() {
                let note = "";
                if (!this.isCustomIncentive) {
                    note =
                        this.incentiveTypeId === INCENTIVE_TYPE.Flat
                            ? this.incentiveAmountRoundingNote
                            : this.incentiveRateRoundingNote;
                }
                return note;
            },
            incentiveValueDataFieldLabel() {
                if (this.isFlatIncentive) {
                    return "";
                } else if (this.isPbiIncentive) {
                    return this.productionDataFieldLabel;
                } else {
                    return this.ratingDataFieldLabel;
                }
            },
            incentiveValueDataFields() {
                if (this.isFlatIncentive) {
                    return [];
                } else if (this.isPbiIncentive) {
                    return this.productionDataFields;
                } else {
                    return this.ratingDataFields;
                }
            },
            maximumIncentivizedRatingLabel() {
                return this.optionDefinition(OPTION.MaximumIncentivizedRating).IsUsed
                    ? this.maximumIncentivizedRatingInputLabel
                    : this.maximumIncentivizedRatingOptionLabel;
            },
            maximumAmountCapLabel() {
                return this.optionDefinition(OPTION.MaximumAmountCap).IsUsed
                    ? this.maximumAmountCapInputLabel
                    : this.maximumAmountCapOptionLabel;
            },
            approvedAmountCapOptionIsAvailable() {
                return (
                    this.optionIsAvailable(OPTION.ApprovedAmountCap) &&
                    // The Approved Amount Cap is only applicable to a Flat
                    //  incentive type if the Formula option is being used
                    (!this.isFlatIncentive || this.useIncentiveValueFormula)
                );
            },
            isNormalAdminEdit() {
                return !this.viewOnly && this.isEditable;
            },
            isCpAdminOverrideEdit() {
                // CPWriteAdmins can edit IncentiveDesigns which have been Enabled and thus are
                //  not editable by regular ProgramAdmins
                return (
                    !this.viewOnly &&
                    !this.isEditable &&
                    this.$app.loggedInUserIsCPAdmin &&
                    !this.$app.loggedInUserIsReadOnly
                );
            },
            cancelOrCloseButtonText() {
                return this.viewOnly ? this.closeButtonText : this.cancelButtonText;
            },
            showSaveIncentiveError() {
                return this.saveIncentiveDesignError != "";
            },
            errorSeverity() {
                return ALERT_SEVERITY.error;
            },
            // Localization
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        watch: {
            useStartDate() {
                if (!this.useStartDate) {
                    this.startDateString = "";
                    this.effectiveDateRangeError = false;
                }
            },
            useExpirationDate() {
                if (!this.useExpirationDate) {
                    this.expirationDateString = "";
                    this.effectiveDateRangeError = false;
                }
            }
        },
        created() {
            this.OPTION = OPTION;
            this.PARAM = PARAM;
        },
        methods: {
            onLoaded(model) {
                this.programId = model.ProgramPublicId;
                this.programTimeZone = model.ProgramTimeZone;
                this.viewOnly = model.ViewOnly;
                this.incentiveDesignId = model.IncentiveDesignPublicId;

                this.incentiveName = model.IncentiveDesignConfig.IncentiveName || "";
                this.incentiveTypeId =
                    model.IncentiveDesignPublicId === NEW_INCENTIVE_DESIGN_PUBLIC_ID
                        ? ""
                        : model.IncentiveDesignConfig.IncentiveTypeId;
                this.isEditable = model.IncentiveDesignConfig.IsEditable;
                (this.startDateString = model.IncentiveDesignConfig.StartDateDisplay),
                    (this.useStartDate = this.startDateString != ""),
                    (this.expirationDateString = model.IncentiveDesignConfig.ExpirationDateDisplay),
                    (this.useExpirationDate = this.expirationDateString != "");
                this.conditions = model.IncentiveDesignConfig.Conditions;
                this.incentiveMetadata = model.IncentiveDesignConfig.IncentiveMetadataList;
                this.optionDefinitions = model.IncentiveDesignConfig.OptionDefinitions;

                this.incentiveDesignListUrl = model.IncentiveDesignListUrl;
                this.incentiveDesignTypes = model.IncentiveDesignTypes;
                this.dataFields = model.ConditionDataFields;
                this.operators = {
                    Numeric: model.NumericOperators,
                    String: model.StringOperators,
                    Choice: model.ChoiceOperators,
                    Date: model.DateOperators,
                    Boolean: model.BooleanOperators
                };
                this.listCollection = model.ListCollection;
                this.metadataFields = model.MetadataFields;
                this.useIncentiveValueFormula = this.optionDefinition(OPTION.IncentiveValueFormula).FormulaId ? 1 : 0;
                this.loaded = true;
            },
            optionIsAvailable(option) {
                return (this.availableOptions & option) === option;
            },
            optionDefinition(option) {
                if (this.optionDefinitions) {
                    return this.optionDefinitions.find((o) => o.Option === option);
                }
                return this.emptyOptionDefinition(option, 0);
            },
            emptyOptionDefinition(optionValue, parametersValue) {
                return {
                    Option: optionValue,
                    Parameters: parametersValue,
                    IsUsed: false,
                    DataFieldId: null,
                    FormulaId: null,
                    Value: null,
                    OtherValue: null
                };
            },
            noSelectionOptionText(numOptions) {
                if (numOptions > 0) {
                    return this.genericNoSelectionOption;
                } else {
                    return this.noAvailableSelectionOption;
                }
            },
            incentiveValueLabel(tier) {
                let label = this.incentiveFlatAmountLabel;
                if (this.isPbiIncentive) {
                    if (this.isTieredIncentive) {
                        label = tier === 1 ? this.incentiveTier1PbiRateLabel : this.incentiveTier2PbiRateLabel;
                    } else {
                        label = this.incentivePbiRateLabel;
                    }
                } else if (this.isEpbbIncentive) {
                    if (this.isTieredIncentive) {
                        label = tier === 1 ? this.incentiveTier1EpbbRateLabel : this.incentiveTier2EpbbRateLabel;
                    } else {
                        label = this.incentiveEpbbRateLabel;
                    }
                }
                return label;
            },
            setNameValidated() {
                this.nameValidated = true;
            },
            onEffectiveDateChanged() {
                this.effectiveDateRangeError = !this.validateEffectiveDates();
                this.setFormDirty();
            },
            validateEffectiveDates() {
                // If either date input is missing, then there is no date "range" to check
                if (!this.startDateString || !this.expirationDateString) {
                    return true;
                }
                const startDate = dateStrToClientDate(this.$app, this.startDateString);
                const expirationDate = dateStrToClientDate(this.$app, this.expirationDateString);
                // If either date input is not a valid date, fail
                if (startDate === null || expirationDate === null) {
                    return false;
                }
                // The Expiration Date must be strictly after Start Date
                return expirationDate.isAfter(startDate, true);
            },
            onTierRatingCutoffChanged(opt, value) {
                this.setParameter(opt, PARAM.Value, value);
                if (this.optionIsAvailable(OPTION.Tier2RatingCutoff)) {
                    this.tierRatingCutoffError = !this.validateTierRatingCutoffs();
                }
            },
            validateTierRatingCutoffs() {
                if (!this.optionIsAvailable(OPTION.Tier2RatingCutoff)) {
                    return true;
                }
                const tier1Cutoff = this.optionDefinition(OPTION.Tier1RatingCutoff).Value;
                const tier2Cutoff = this.optionDefinition(OPTION.Tier2RatingCutoff).Value;
                if (!tier1Cutoff || !tier2Cutoff) {
                    return true;
                }
                const tier1CutoffValue = Number(tier1Cutoff);
                const tier2CutoffValue = Number(tier2Cutoff);
                if (Number.isNaN(tier1CutoffValue) || Number.isNaN(tier2CutoffValue)) {
                    return true;
                }
                // The Tier2 Cutoff value must be strictly greater than the Tier1 Cutoff value
                return tier2CutoffValue > tier1CutoffValue;
            },
            onIncentiveTypeChanged() {
                // Clear all the available option inputs
                this.optionDefinitions.forEach((opt) => {
                    if (this.optionIsAvailable(opt.Option)) {
                        const empty = this.emptyOptionDefinition(opt.Option, opt.Parameters);
                        for (const [key] of Object.entries(empty)) {
                            opt[key] = empty[key];
                        }
                    }
                });
                // If a formula selection is not available for this option, make sure using a formula is unselected
                if (!this.incentiveValueFormulaOptionIsAvailable) {
                    this.useIncentiveValueFormula = 0;
                }
                this.validationHub.clearValidation();
                if (this.nameValidated) {
                    this.$refs.IncentiveNameTextbox.validate();
                    this.$refs.StartDateInput.validate();
                    this.$refs.ExpirationDateInput.validate();
                }
                this.setFormDirty();
            },
            setParameter(opt, param, value) {
                const i = this.optionDefinitions.findIndex((o) => o.Option === opt);
                this.optionDefinitions[i][param] = value;
                this.setFormDirty();
            },
            addCondition(condition) {
                this.conditions.push(condition);
                this.setFormDirty();
            },
            modifyCondition(condition) {
                const i = this.conditions.findIndex((c) => c.ConditionGroupId === condition.ConditionGroupId);
                this.conditions[i] = condition;
                this.setFormDirty();
            },
            removeCondition(conditionId) {
                const i = this.conditions.findIndex((c) => c.ConditionGroupId === conditionId);
                if (conditionId.startsWith(NEW_CONDITION_TAG)) {
                    this.conditions.splice(i, 1);
                } else {
                    this.conditions[i].IsDeleted = true;
                }
                this.setFormDirty();
            },
            cancelChanges() {
                this.$app.setUnsavedChanges(false);
                this.returnToIncentiveDesignList();
            },
            async saveDesign() {
                // Giant warning if edited after being enabled
                if (this.isCpAdminOverrideEdit || !this.isEditable) {
                    const confirmResult = await this.$app.showDialog({
                        title: "WARNING",
                        customComponent: textConfirmationDialog,
                        customComponentOptions: {
                            message:
                                "EDITING ENABLED INCENTIVES IS LIKELY TO BREAK SOMETHING. " +
                                "Are you sure you want to continue?",
                            requiredConfirmation: "I know what I'm doing"
                        }
                    });
                    if (confirmResult !== TEXT_CONFIRMATION_RESULT.ok) {
                        return;
                    }
                }
                this.saveIncentiveDesignError = "";
                this.validationResults = this.validationHub.validate();
                this.nameValidated = true;
                if (this.effectiveDateRangeError || (this.validationResults && this.validationResults.length > 0)) {
                    return;
                }
                this.$app.blockUi("Saving...");
                try {
                    const config = {
                        IncentiveName: this.incentiveName,
                        IncentiveTypeId: this.incentiveTypeId,
                        StartDateDisplay: this.startDateString,
                        ExpirationDateDisplay: this.expirationDateString,
                        Conditions: this.conditions,
                        OptionDefinitions: this.optionDefinitions,
                        IncentiveMetadataList: this.incentiveMetadata
                    };
                    const result = await this.SaveIncentiveDesign2({
                        programId: this.programId,
                        incentiveDesignId: this.incentiveDesignId,
                        incentiveDesignConfig: config
                    });
                    const resultData = JSON.parse(result.Data);
                    if (resultData.status[0] === "success") {
                        this.$app.setUnsavedChanges(false);
                        this.returnToIncentiveDesignList();
                    } else {
                        this.saveIncentiveDesignError = resultData.error[0];
                    }
                } finally {
                    this.$app.unBlockUi();
                }
            },
            returnToIncentiveDesignList() {
                this.$app.setHref(this.incentiveDesignListUrl);
            },
            setFormDirty() {
                if (this.loaded) {
                    this.$app.setUnsavedChanges(true);
                }
            }
        }
    };
</script>
<style lang="scss">
    .no-left-padding {
        padding-left: 0% !important;
    }
</style>
